import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, SkeletonList, QueryError } from '../../../../components/ui';
import { listActions } from '../../../../lib/actions';

export function TopDraftsWidget() {
  const navigate = useNavigate();
  const basePath = useBasePath();

  const pendingDrafts = useQuery<Array<{ id: string; title: string; severity?: string; payload?: Record<string, unknown> }>>({
    queryKey: ['pending-drafts'],
    queryFn: async () => {
      const rows = await listActions();
      return rows.filter(a => a.kind === 'gmail_followup_draft' && a.status === 'proposed').map(a => ({
        id: a.id, title: a.title, severity: a.severity ?? undefined, payload: a.payload ?? undefined,
      }));
    },
    staleTime: 60_000,
    retry: 0,
  });

  const drafts = pendingDrafts.data ?? [];

  return (
    <DataCard
      title="Drafts Waiting"
      subtitle="AI-drafted emails ready to approve and send"
      badge={drafts.length > 0 ? <StatusBadge status="warning" label={`${drafts.length}`} size="xs" /> : undefined}
    >
      {pendingDrafts.isLoading ? <SkeletonList rows={2} /> : pendingDrafts.isError ? <QueryError error={pendingDrafts.error} onRetry={() => pendingDrafts.refetch()} compact /> : drafts.length === 0 ? (
        <p className="text-2xs text-ink-dim italic">No drafts waiting. New ones surface automatically as signals arrive.</p>
      ) : (
        <div className="space-y-1.5">
          {drafts.slice(0, 4).map(d => (
            <div key={d.id}
              onClick={() => navigate(`${basePath}/actions`)}
              className="flex items-start justify-between gap-2 rounded-lg bg-surface-1 px-3 py-2 cursor-pointer hover:bg-surface-2 transition-colors">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-ink">{d.title}</p>
                {d.payload?.to ? <p className="truncate text-2xs text-ink-dim">To {String(d.payload.to)}</p> : null}
              </div>
              <StatusBadge status={d.severity === 'high' ? 'critical' : d.severity === 'med' ? 'warning' : 'info'} label={d.severity ?? 'med'} size="xs" />
            </div>
          ))}
        </div>
      )}
    </DataCard>
  );
}
